import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  Button,
  InputBase,
  Box,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Popover } from "@material-ui/core";
import { Settings } from "@material-ui/icons";

import { RootState } from "../../store/store";

import Loader from "../layout/Loader";

function ImageUploader(props: UploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [originalFileExtension, setOriginalFileExtension] =
    useState<string>("");
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [convertToWebp, setConvertToWebp] = useState(true);
  const [maintainQuality, setMaintainQuality] = useState(false);
  const [isImageFile, setIsImageFile] = useState(true);
  const [dimensions, setDimensions] = useState({
    large: { width: "", height: "" },
    medium: { width: "", height: "" },
    small: { width: "", height: "" },
  });
  const [uploading, setUploading] = useState(false);

  const state = useSelector((state: RootState) => state);

  const handleOpenAdvancedMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAdvancedMenu = () => {
    setAnchorEl(null);
  };

  const handleDimensionChange = (
    type: "large" | "medium" | "small",
    dimension: "width" | "height",
    value: string
  ) => {
    setDimensions((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [dimension]: value,
      },
    }));
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleAssetPath = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      const originalFileExtension = file.name.split(".").pop()?.toLowerCase();
      const isImage = ["jpg", "jpeg", "png", "webp"].includes(
        originalFileExtension as string
      );

      setIsImageFile(isImage);

      if (
        !isImage &&
        !["pdf", "svg"].includes(originalFileExtension as string)
      ) {
        resetFileInput();
        alert("Only JPG, JPEG, PNG, WEBP, SVG, and PDF files are supported.");
        return;
      }

      const fileName = file.name
        .split(".")[0]
        .toLowerCase()
        .replace(/\s+/g, "-") // Replaces one or more whitespace characters (spaces, tabs, newlines) with a hyphen ("-").
        .replace(/_+/g, "-") // Replaces one or more underscores ("_") with a hyphen ("-").
        .replace(/-+/g, "-") // Replaces one or more consecutive hyphens ("-") with a single hyphen ("-").
        .replace(/[^a-z0-9-]+/g, "") // Removes any character that is NOT a lowercase letter (a-z), a number (0-9), or a hyphen ("-").
        .replace(/\s+/g, "-"); // Replaces any remaining whitespace characters (spaces, tabs, newlines) with a hyphen ("-").
      setFileName(fileName);
      setOriginalFileExtension(originalFileExtension as string);
      setOriginalFile(file);

      if (isImage) {
        const image = new Image();
        image.src = URL.createObjectURL(file);
        image.onload = () => {
          setDimensions({
            large: {
              width: image.width.toString(),
              height: image.height.toString(),
            },
            medium: {
              width: (image.width * 0.75).toString(),
              height: (image.height * 0.75).toString(),
            },
            small: {
              width: (image.width * 0.5).toString(),
              height: (image.height * 0.5).toString(),
            },
          });
        };
        image.onerror = () => {
          console.error("Error loading image:", file);
        };
      } else {
        setConvertToWebp(false);
        setMaintainQuality(false);
        setDimensions({
          large: { width: "", height: "" },
          medium: { width: "", height: "" },
          small: { width: "", height: "" },
        });
      }
    }
  };

  const handleEditFileName = (fileName: string) => {
    fileName = fileName
      .replace(/\s+/g, "-")
      .replace(/_+/g, "-")
      .replace(/-+/g, "-")
      .replace(/[^a-z0-9\-]+/g, "")
      .replace(/\s+/g, "-");
    setFileName(fileName);
    return fileName;
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFile = async () => {
    setUploading(true);
    const formData = new FormData();

    if (!originalFile) {
      alert("No file selected.");
      setUploading(false);
      return;
    }

    formData.append("file", originalFile);
    formData.append(
      "format",
      isImageFile && convertToWebp ? "webp" : originalFileExtension
    );
    formData.append("rawLargeRes", JSON.stringify(dimensions.large));
    formData.append("rawMediumRes", JSON.stringify(dimensions.medium));
    formData.append("rawSmallRes", JSON.stringify(dimensions.small));
    formData.append("quality", isImageFile && maintainQuality ? "100" : "80");
    formData.append("instance", state.current_site.site?.slug!);
    formData.append(
      "assetPath",
      `${originalFileExtension === "pdf" ? "pdf" : "images"}/${fileName}.${isImageFile && convertToWebp ? "webp" : originalFileExtension}`
    );
    formData.append("newFileName", fileName || "");

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/asset`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${state.current_user.user?.token}`,
          instance: state.current_site.site?.slug!,
          "asset-path": `${originalFileExtension === "pdf" ? "pdf" : "images"}/${fileName}.${isImageFile && convertToWebp ? "webp" : originalFileExtension}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed.");
      }

      window.location.reload();
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        mt: 2,
        gap: 2,
      }}
    >
      {uploading ? <Loader open={true} /> : null}

      <Paper
        component="form"
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          height: 56,
          width: "100%",
          fontFamily: "monospace",
        }}
      >
        {originalFile && (
          <Typography fontFamily={"monospace"}>
            assets/{originalFileExtension === "pdf" ? "pdf" : "images"}/
          </Typography>
        )}
        <InputBase
          placeholder={originalFile ? "file_name" : "Select a file"}
          name="path"
          value={fileName || ""}
          disabled={!originalFile}
          onChange={(e) => handleEditFileName(e.target.value)}
          sx={{
            fontSize: "16px",
            textAlign: "center",
            fontFamily: "monospace",
            fontKerning: "none",
            width: `${originalFile ? `${((fileName?.length || 9) * 9).toFixed(0)}px` : "100%"}`,
            maxWidth: "100%",
            whiteSpace: "nowrap",
          }}
        />{" "}
        {originalFile && (
          <Typography fontFamily={"monospace"}>
            .{isImageFile && convertToWebp ? "webp" : originalFileExtension}
          </Typography>
        )}
      </Paper>

      {originalFile ? (
        isImageFile || originalFileExtension === "svg" ? (
          <div style={{ position: "relative", width: "100%" }}>
            <Button
              variant="contained"
              color="primary"
              style={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
              onClick={handleOpenAdvancedMenu}
            >
              <Settings style={{ margin: 0, padding: 0 }} />
            </Button>

            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleCloseAdvancedMenu}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              style={{ marginTop: 8 }}
            >
              <Paper
                sx={{
                  width: 300,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "start",
                }}
              >
                <Typography
                  sx={{ margin: "0 auto", mb: 1, fontWeight: "bold" }}
                >
                  Advanced Settings
                </Typography>
                {["large", "medium", "small"].map((type) => (
                  <Box
                    key={type}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      justifyContent: "start",
                    }}
                  >
                    <Typography sx={{ mr: 1, width: "60px" }}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Typography>
                    <InputBase
                      sx={{
                        mr: 1,
                        width: 50,
                        border: "1px solid #ccc",
                      }}
                      placeholder="Width"
                      value={dimensions[type as keyof typeof dimensions].width}
                      onChange={(e) =>
                        handleDimensionChange(
                          type as keyof typeof dimensions,
                          "width",
                          e.target.value
                        )
                      }
                      inputProps={{
                        style: { textAlign: "center" },
                      }}
                    />
                    <Typography sx={{ mr: 1 }}>x</Typography>
                    <InputBase
                      sx={{ mr: 1, width: 50, border: "1px solid #ccc" }}
                      placeholder="Height"
                      value={dimensions[type as keyof typeof dimensions].height}
                      onChange={(e) =>
                        handleDimensionChange(
                          type as keyof typeof dimensions,
                          "height",
                          e.target.value
                        )
                      }
                      inputProps={{
                        style: { textAlign: "center" },
                      }}
                    />
                  </Box>
                ))}
                <FormControlLabel
                  control={
                    <Switch
                      checked={convertToWebp}
                      onChange={(e) => setConvertToWebp(e.target.checked)}
                      disabled={!isImageFile}
                    />
                  }
                  label="Convert to WEBP"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={maintainQuality}
                      onChange={(e) => setMaintainQuality(e.target.checked)}
                      disabled={!isImageFile}
                    />
                  }
                  label="Maintain Original Quality"
                />
              </Paper>
            </Popover>

            <Box
              component="img"
              alt="upload"
              src={URL.createObjectURL(originalFile)}
              sx={{
                height: "60vh",
                objectFit: "contain",
                width: "100%",
                border: "1px dashed lightgrey",
                borderRadius: "5px",
              }}
            />
          </div>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px dashed lightgrey",
              borderRadius: "5px",
              width: "100%",
              height: "60vh",
            }}
          >
            {/* PDF Icon SVG */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32em"
              height="32em"
              viewBox="0 0 32 32"
            >
              <path
                fill="#909090"
                d="m24.1 2.072l5.564 5.8v22.056H8.879V30h20.856V7.945z"
              />
              <path fill="#f4f4f4" d="M24.031 2H8.808v27.928h20.856V7.873z" />
              <path fill="#7a7b7c" d="M8.655 3.5h-6.39v6.827h20.1V3.5z" />
              <path fill="#dd2025" d="M22.472 10.211H2.395V3.379h20.077z" />
              <path
                fill="#464648"
                d="M9.052 4.534H7.745v4.8h1.028V7.715L9 7.728a2 2 0 0 0 .647-.117a1.4 1.4 0 0 0 .493-.291a1.2 1.2 0 0 0 .335-.454a2.1 2.1 0 0 0 .105-.908a2.2 2.2 0 0 0-.114-.644a1.17 1.17 0 0 0-.687-.65a2 2 0 0 0-.409-.104a2 2 0 0 0-.319-.026m-.189 2.294h-.089v-1.48h.193a.57.57 0 0 1 .459.181a.92.92 0 0 1 .183.558c0 .246 0 .469-.222.626a.94.94 0 0 1-.524.114m3.671-2.306c-.111 0-.219.008-.295.011L12 4.538h-.78v4.8h.918a2.7 2.7 0 0 0 1.028-.175a1.7 1.7 0 0 0 .68-.491a1.9 1.9 0 0 0 .373-.749a3.7 3.7 0 0 0 .114-.949a4.4 4.4 0 0 0-.087-1.127a1.8 1.8 0 0 0-.4-.733a1.6 1.6 0 0 0-.535-.4a2.4 2.4 0 0 0-.549-.178a1.3 1.3 0 0 0-.228-.017m-.182 3.937h-.1V5.392h.013a1.06 1.06 0 0 1 .6.107a1.2 1.2 0 0 1 .324.4a1.3 1.3 0 0 1 .142.526c.009.22 0 .4 0 .549a3 3 0 0 1-.033.513a1.8 1.8 0 0 1-.169.5a1.1 1.1 0 0 1-.363.36a.67.67 0 0 1-.416.106m5.08-3.915H15v4.8h1.028V7.434h1.3v-.892h-1.3V5.43h1.4v-.892"
              />
              <path
                fill="#dd2025"
                d="M21.781 20.255s3.188-.578 3.188.511s-1.975.646-3.188-.511m-2.357.083a7.5 7.5 0 0 0-1.473.489l.4-.9c.4-.9.815-2.127.815-2.127a14 14 0 0 0 1.658 2.252a13 13 0 0 0-1.4.288Zm-1.262-6.5c0-.949.307-1.208.546-1.208s.508.115.517.939a10.8 10.8 0 0 1-.517 2.434a4.4 4.4 0 0 1-.547-2.162Zm-4.649 10.516c-.978-.585 2.051-2.386 2.6-2.444c-.003.001-1.576 3.056-2.6 2.444M25.9 20.895c-.01-.1-.1-1.207-2.07-1.16a14 14 0 0 0-2.453.173a12.5 12.5 0 0 1-2.012-2.655a11.8 11.8 0 0 0 .623-3.1c-.029-1.2-.316-1.888-1.236-1.878s-1.054.815-.933 2.013a9.3 9.3 0 0 0 .665 2.338s-.425 1.323-.987 2.639s-.946 2.006-.946 2.006a9.6 9.6 0 0 0-2.725 1.4c-.824.767-1.159 1.356-.725 1.945c.374.508 1.683.623 2.853-.91a23 23 0 0 0 1.7-2.492s1.784-.489 2.339-.623s1.226-.24 1.226-.24s1.629 1.639 3.2 1.581s1.495-.939 1.485-1.035"
              />
              <path fill="#909090" d="M23.954 2.077V7.95h5.633z" />
              <path fill="#f4f4f4" d="M24.031 2v5.873h5.633z" />
              <path
                fill="#fff"
                d="M8.975 4.457H7.668v4.8H8.7V7.639l.228.013a2 2 0 0 0 .647-.117a1.4 1.4 0 0 0 .493-.291a1.2 1.2 0 0 0 .332-.454a2.1 2.1 0 0 0 .105-.908a2.2 2.2 0 0 0-.114-.644a1.17 1.17 0 0 0-.687-.65a2 2 0 0 0-.411-.105a2 2 0 0 0-.319-.026m-.189 2.294h-.089v-1.48h.194a.57.57 0 0 1 .459.181a.92.92 0 0 1 .183.558c0 .246 0 .469-.222.626a.94.94 0 0 1-.524.114m3.67-2.306c-.111 0-.219.008-.295.011l-.235.006h-.78v4.8h.918a2.7 2.7 0 0 0 1.028-.175a1.7 1.7 0 0 0 .68-.491a1.9 1.9 0 0 0 .373-.749a3.7 3.7 0 0 0 .114-.949a4.4 4.4 0 0 0-.087-1.127a1.8 1.8 0 0 0-.4-.733a1.6 1.6 0 0 0-.535-.4a2.4 2.4 0 0 0-.549-.178a1.3 1.3 0 0 0-.228-.017m-.182 3.937h-.1V5.315h.013a1.06 1.06 0 0 1 .6.107a1.2 1.2 0 0 1 .324.4a1.3 1.3 0 0 1 .142.526c.009.22 0 .4 0 .549a3 3 0 0 1-.033.513a1.8 1.8 0 0 1-.169.5a1.1 1.1 0 0 1-.363.36a.67.67 0 0 1-.416.106m5.077-3.915h-2.43v4.8h1.028V7.357h1.3v-.892h-1.3V5.353h1.4v-.892"
              />
            </svg>
          </Box>
        )
      ) : (
        <Paper component="form" sx={{ width: "100%" }}>
          <Box
            sx={{
              position: "relative",
              border: "1px dashed lightgrey",
              borderRadius: "5px",
              width: "100%",
              height: "60vh",
            }}
          >
            <Box
              ref={fileInputRef}
              component="input"
              type="file"
              name="file"
              sx={{
                width: "100%",
                height: "100%",
                opacity: 0,
                position: "relative",
                zIndex: 99,
                cursor: "pointer",
              }}
              onChange={handleAssetPath}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <GetAppIcon sx={{ fontSize: 50, color: "lightgrey", mb: 1 }} />
              <Typography>
                Choose an image or PDF file, or drag it here.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Button
        sx={{ height: 56, width: "100%" }}
        variant="contained"
        color="primary"
        disabled={!originalFile || uploading}
        onClick={uploadFile}
      >
        {uploading ? "Uploading..." : "Upload File"}
      </Button>
    </Box>
  );
}

interface UploaderProps {
  handleClose: () => void;
  onUpload: (newAsset: any) => void;
  setImagePath?: (imagePath: string) => void;
  assetPath?: string;
}

export default ImageUploader;
